// utils/purchasePdf.js
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export function downloadPurchasePdf({ formData, currency, files, logoDataUrl, meta = {} }) {
  const doc = new jsPDF({ unit: "pt", compress: true, format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const primary = [26, 35, 126]; // #1A237E (Navy Blue to match PO)
  const secondary = [69, 90, 100]; // #455A64
  const margin = 40;

  doc.setProperties({
    title: meta.title || "Purchase Request",
  });

  // helpers
  const safe = (v, fallback = "—") => (v === 0 || v ? String(v) : fallback);
  const getName = (v) => (v || "").split("|")[0] || "—";
  const fmtMoney = (v) => {
    const n = Number(v);
    return Number.isFinite(n)
      ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : safe(v);
  };
  const money = (v) => [currency || "", fmtMoney(v)].filter(Boolean).join(" ").trim();

  // 1. Top Accent Bar
  doc.setFillColor(...primary);
  doc.rect(0, 0, pageWidth, 6, "F");

  // 2. Header Row
  let cursorY = 45;
  
  if (logoDataUrl) {
    const w = 110, h = 28;
    doc.addImage(logoDataUrl, "PNG", margin, cursorY, w, h, undefined, "FAST");
  }

  doc.setFont("helvetica", "bold");
  doc.setTextColor(...primary);
  doc.setFontSize(22);
  doc.text(meta.title ? meta.title.toUpperCase() : "PURCHASE REQUISITION", pageWidth - margin, cursorY + 20, { align: "right" });
  
  if (meta.requestId) {
    doc.setFontSize(10);
    doc.setTextColor(...secondary);
    doc.text(`PR NO: ${meta.requestId}`, pageWidth - margin, cursorY + 35, { align: "right" });
  }

  // 3. Divider
  cursorY += 50;
  doc.setDrawColor(207, 216, 220);
  doc.setLineWidth(1);
  doc.line(margin, cursorY, pageWidth - margin, cursorY);
  
  // 4. Requester & General Info (Side-by-side)
  cursorY += 25;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...primary);
  doc.text("REQUESTER DETAILS", margin, cursorY);
  doc.text("PURCHASE DETAILS", pageWidth / 2 + 10, cursorY);

  cursorY += 15;
  doc.setFontSize(9);
  
  // Left Column (Requester)
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...secondary);
  doc.text("Name:", margin, cursorY);
  doc.text("Location:", margin, cursorY + 15);
  doc.text("Expected Date:", margin, cursorY + 30);
  
  doc.setFont("helvetica", "normal");
  doc.setTextColor(33, 33, 33);
  doc.text(safe(meta.requesterName), margin + 80, cursorY);
  doc.text(safe(formData.location), margin + 80, cursorY + 15);
  doc.text(safe(formData.expectedDate), margin + 80, cursorY + 30);

  // Right Column (Purchase)
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...secondary);
  doc.text("Category:", pageWidth / 2 + 10, cursorY);
  doc.text("Type:", pageWidth / 2 + 10, cursorY + 15);
  doc.text("Payment:", pageWidth / 2 + 10, cursorY + 30);
  
  doc.setFont("helvetica", "normal");
  doc.setTextColor(33, 33, 33);
  doc.text(safe(formData.productCategory), pageWidth / 2 + 80, cursorY);
  doc.text(safe(formData.purchaseType), pageWidth / 2 + 80, cursorY + 15);
  doc.text(safe(formData.paymentType), pageWidth / 2 + 80, cursorY + 30);

  // 5. Item Table
  cursorY += 60;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...primary);
  doc.text("ITEM BREAKDOWN", margin, cursorY);

  autoTable(doc, {
    startY: cursorY + 8,
    head: [["Product Name", "Description", "Qty", "Unit Price", "Total Cost"]],
    body: [
      [
        safe(formData.productName), 
        safe(formData.productDescription), 
        safe(formData.quantity), 
        money(formData.unitPrice), 
        money(formData.approxCost)
      ]
    ],
    styles: { font: "helvetica", fontSize: 9, cellPadding: 8, textColor: [33, 33, 33] },
    headStyles: { fillColor: primary, textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    columnStyles: { 
      2: { halign: "center" },
      3: { halign: "right" },
      4: { halign: "right", fontStyle: "bold" } 
    },
    tableWidth: pageWidth - margin * 2,
    margin: { left: margin, right: margin },
  });

  // 6. Justification & Approvals Table
  let finalY = doc.lastAutoTable.finalY + 30;
  
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...primary);
  doc.text("JUSTIFICATION & APPROVALS", margin, finalY);

  const filesList = files && files.length ? Array.from(files).map((f) => f.name).join(", ") : "None";

  autoTable(doc, {
    startY: finalY + 8,
    body: [
      ["Purchase Reason", safe(formData.productReason)],
      ["Additional Comments", safe(formData.comment)],
      ["Attachments", filesList],
      ["Level 1 Approver", getName(formData.reportingTo)],
      ["Level 2 Approver", getName(formData.finance)],
      ["Level 3 Approver", getName(formData.higher_authority)],
    ],
    styles: { font: "helvetica", fontSize: 9, cellPadding: 8, textColor: [33, 33, 33] },
    columnStyles: { 
      0: { cellWidth: 120, fontStyle: "bold", fillColor: [245, 247, 250], textColor: secondary } 
    },
    tableWidth: pageWidth - margin * 2,
    margin: { left: margin, right: margin },
  });

  // 7. Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    // Bottom Bar
    doc.setFillColor(...primary);
    doc.rect(margin, pageHeight - 35, pageWidth - margin * 2, 2, "F");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(158, 158, 158);
    doc.text("This is a computer-generated PR document.", margin, pageHeight - 20);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 20, { align: "right" });
  }

  doc.save("purchase-request.pdf");
}
